import { Spinner, Text } from '@viapeople/components'

export interface EmployeeTableRow {
	userId: string
	employeeId: string
	employeeName: string
	title: string | null
	department: string | null
	businessUnit: string | null
	location: string | null
	managerName: string | null
	hireDate: string | null
	tenureYears: number | null
	avgRating: number | null
	threeSixtyAvg: number | null
	goalCompletionPercent: number | null
	performanceCategory: 'High' | 'Solid' | 'Needs Improvement' | null
	cycleName: string | null
	cycleLaunchDate: string | null
	cycleDeadline: string | null
}

interface EmployeeTableProps {
	rows: EmployeeTableRow[] | undefined
	total?: number
	loading?: boolean
	onExportCsv?: () => void
	exporting?: boolean
	page?: number
	pageSize?: number
	onPageChange?: (page: number) => void
}

export function EmployeeTable({
	rows,
	total,
	loading,
	onExportCsv,
	exporting,
	page,
	pageSize,
	onPageChange,
}: EmployeeTableProps) {
	if (loading) {
		return (
			<div data-testid='employee-table-loading' className='flex items-center justify-center h-32'>
				<Spinner />
			</div>
		)
	}
	if (!rows || rows.length === 0) {
		return (
			<div data-testid='employee-table-empty' className='flex items-center justify-center h-32 text-neutral-500'>
				No employees match the current filters.
			</div>
		)
	}

	const paginationEnabled = typeof page === 'number' && typeof pageSize === 'number' && !!onPageChange
	const totalPages = paginationEnabled && total != null ? Math.max(1, Math.ceil(total / (pageSize as number))) : 1
	const currentPage = paginationEnabled ? (page as number) : 0
	const canPrev = paginationEnabled && currentPage > 0
	const canNext = paginationEnabled && currentPage < totalPages - 1

	return (
		<div data-testid='employee-table' className='w-full overflow-x-auto'>
			<div className='flex items-center justify-between mb-2'>
				<Text variant='small' weight='semibold'>
					Employees ({total != null && total > rows.length ? `${rows.length} of ${total}` : rows.length})
				</Text>
				{onExportCsv && (
					<button
						type='button'
						data-testid='employee-table-export-csv'
						onClick={onExportCsv}
						disabled={exporting}
						className='text-xs px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
						{exporting
							? 'Exporting…'
							: `Export CSV${total != null && total > (rows?.length ?? 0) ? ' (all rows)' : ''}`}
					</button>
				)}
			</div>
			<table className='min-w-full text-xs'>
				<thead className='bg-neutral-100 text-neutral-700'>
					<tr>
						<th className='text-left px-3 py-2'>Employee</th>
						<th className='text-left px-3 py-2'>Employee ID</th>
						<th className='text-left px-3 py-2'>Title</th>
						<th className='text-left px-3 py-2'>Department</th>
						<th className='text-left px-3 py-2'>Business Unit</th>
						<th className='text-left px-3 py-2'>Location</th>
						<th className='text-left px-3 py-2'>Manager</th>
						<th className='text-left px-3 py-2'>Hire Date</th>
						<th className='text-right px-3 py-2'>Tenure (yrs)</th>
						<th className='text-right px-3 py-2'>Avg Rating</th>
						<th className='text-right px-3 py-2'>360 Avg</th>
						<th className='text-right px-3 py-2'>Goal %</th>
						<th className='text-left px-3 py-2'>Category</th>
						<th className='text-left px-3 py-2'>Cycle</th>
						<th className='text-left px-3 py-2'>Review Period</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((row) => {
						const reviewPeriod =
							row.cycleLaunchDate && row.cycleDeadline
								? `${row.cycleLaunchDate} → ${row.cycleDeadline}`
								: row.cycleLaunchDate || row.cycleDeadline || '—'
						// Composite key: a user can appear once per cycle when multiple cycles are filtered.
						const rowKey = `${row.userId}|${row.cycleName ?? ''}|${row.cycleLaunchDate ?? ''}`
						return (
							<tr key={rowKey} data-testid={`employee-row-${rowKey}`} className='border-b border-neutral-200'>
								<td className='px-3 py-2 font-medium'>{row.employeeName}</td>
								<td className='px-3 py-2 text-neutral-500'>{row.employeeId}</td>
								<td className='px-3 py-2'>{row.title ?? '—'}</td>
								<td className='px-3 py-2'>{row.department ?? '—'}</td>
								<td className='px-3 py-2'>{row.businessUnit ?? '—'}</td>
								<td className='px-3 py-2'>{row.location ?? '—'}</td>
								<td className='px-3 py-2'>{row.managerName ?? '—'}</td>
								<td className='px-3 py-2'>{row.hireDate ?? '—'}</td>
								<td className='px-3 py-2 text-right'>{row.tenureYears ?? '—'}</td>
								<td className='px-3 py-2 text-right'>{row.avgRating?.toFixed(2) ?? '—'}</td>
								<td className='px-3 py-2 text-right'>{row.threeSixtyAvg?.toFixed(2) ?? '—'}</td>
								<td className='px-3 py-2 text-right'>
									{row.goalCompletionPercent != null ? `${row.goalCompletionPercent.toFixed(1)}%` : '—'}
								</td>
								<td className='px-3 py-2'>{row.performanceCategory ?? '—'}</td>
								<td className='px-3 py-2'>{row.cycleName ?? '—'}</td>
								<td className='px-3 py-2'>{reviewPeriod}</td>
							</tr>
						)
					})}
				</tbody>
			</table>
			{paginationEnabled && (
				<div className='flex items-center justify-between mt-2' data-testid='employee-table-pagination'>
					<Text variant='hint' className='text-neutral-500'>
						Page {currentPage + 1} of {totalPages}
					</Text>
					<div className='flex gap-2'>
						<button
							type='button'
							data-testid='employee-table-prev-page'
							onClick={() => onPageChange?.(currentPage - 1)}
							disabled={!canPrev}
							className='text-xs px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
							Previous
						</button>
						<button
							type='button'
							data-testid='employee-table-next-page'
							onClick={() => onPageChange?.(currentPage + 1)}
							disabled={!canNext}
							className='text-xs px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
							Next
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

export function buildCsvFromRows(rows: EmployeeTableRow[]): string {
	const headers = [
		'Employee',
		'Employee ID',
		'Title',
		'Department',
		'Business Unit',
		'Location',
		'Manager',
		'Hire Date',
		'Tenure (years)',
		'Avg Rating',
		'360 Avg',
		'Goal Completion %',
		'Category',
		'Cycle',
		'Review Period',
	]
	// Guard against CSV formula injection: a value that begins with `=`, `+`,
	// `-`, `@`, `\t`, or `\r` is interpreted as a formula by Excel/Sheets when
	// the file is opened. Several columns flow from user-influenced data
	// (employee name, title, department, etc.), so we prepend `'` to neutralize.
	const NEEDS_FORMULA_GUARD = /^[=+\-@\t\r]/
	const escapeCsv = (v: string) => {
		const guarded = NEEDS_FORMULA_GUARD.test(v) ? `'${v}` : v
		return guarded.includes(',') || guarded.includes('"') || guarded.includes('\n') || guarded.includes('\r')
			? `"${guarded.replace(/"/g, '""')}"`
			: guarded
	}
	const lines = [headers.join(',')]
	for (const r of rows) {
		const reviewPeriod =
			r.cycleLaunchDate && r.cycleDeadline
				? `${r.cycleLaunchDate} → ${r.cycleDeadline}`
				: r.cycleLaunchDate || r.cycleDeadline || ''
		const values = [
			r.employeeName,
			r.employeeId,
			r.title ?? '',
			r.department ?? '',
			r.businessUnit ?? '',
			r.location ?? '',
			r.managerName ?? '',
			r.hireDate ?? '',
			r.tenureYears != null ? String(r.tenureYears) : '',
			r.avgRating != null ? r.avgRating.toFixed(2) : '',
			r.threeSixtyAvg != null ? r.threeSixtyAvg.toFixed(2) : '',
			r.goalCompletionPercent != null ? r.goalCompletionPercent.toFixed(1) : '',
			r.performanceCategory ?? '',
			r.cycleName ?? '',
			reviewPeriod,
		]
		lines.push(values.map(escapeCsv).join(','))
	}
	return lines.join('\n')
}
